import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import salesAgentService, { LeadCaptureState, SalesAgent, SalesAgentUpdate, VoiceSettings } from '@/lib/api/salesAgent';
import cartesiaService, { CartesiaVoice } from '@/lib/api/cartesia';
import promptVersionsService from '@/lib/api/promptVersions';
import type { PromptVersion } from '@/lib/api/promptVersions';
import customDomainsService from '@/lib/api/customDomains';
import PromptDiffView from '@/components/prompt-diff/PromptDiffView';
import apiClient from '@/lib/api/client';
import type { FileUploadResponse } from '@/lib/api/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Save, User, Sparkles, Video, FileText, Edit, Calendar, Image, Upload, X, ChevronLeft, ChevronRight, AudioLines, Play, Pause, Search, Globe2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { getEmbeddedDemoUrl, getLiveDemoUrl } from '@/lib/demoUrls';
import { cn } from '@/lib/utils';

const LEAD_CAPTURE_FIELD_OPTIONS: { value: LeadCaptureState; label: string }[] = [
  { value: 'hidden', label: 'Disabled' },
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];

const CONVERSATION_CONTROL_MODE_OPTIONS = [
  {
    value: 'exploratory',
    label: 'Exploratory',
    description: 'Lets the visitor guide more of the path with lighter direction.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Leads when the next step is obvious, but still pauses at real branch points.',
  },
  {
    value: 'directive',
    label: 'Directive',
    description: 'Drives the strongest next proof point proactively with minimal permission-seeking.',
  },
] as const;

const getConversationControlModeOption = (value?: string | null) =>
  CONVERSATION_CONTROL_MODE_OPTIONS.find((option) => option.value === value)
  || CONVERSATION_CONTROL_MODE_OPTIONS.find((option) => option.value === 'balanced')!;

const getLeadCaptureStateLabel = (value?: LeadCaptureState | null) => {
  const option = LEAD_CAPTURE_FIELD_OPTIONS.find((item) => item.value === value);
  return option?.label || 'Disabled';
};

type ConfigSection = 'general' | 'voice' | 'conversation' | 'content' | 'calendar' | 'entryModal' | 'launchState';

const CONFIG_SECTIONS: {
  key: ConfigSection;
  icon: React.FC<{ className?: string }>;
  label: string;
  description: string;
}[] = [
  { key: 'general', icon: User, label: 'General', description: 'Status and operational contact details.' },
  { key: 'voice', icon: AudioLines, label: 'Voice', description: 'Voice selection, style, preview, and cadence.' },
  { key: 'conversation', icon: Sparkles, label: 'Conversation', description: 'Control how strongly the agent leads and how suggestions behave.' },
  { key: 'content', icon: FileText, label: 'Content Sources', description: 'Control which assets the agent can reference.' },
  { key: 'calendar', icon: Calendar, label: 'Calendar', description: 'Booking destination used for follow-up meetings.' },
  { key: 'entryModal', icon: Image, label: 'Entry Modal', description: 'The visitor-facing entry experience before the demo starts.' },
  { key: 'launchState', icon: Image, label: 'Launch State', description: 'What the viewer shows before demo content begins.' },
];

const createEmptyFormData = (): Partial<SalesAgent> => ({
  name: '',
  is_active: true,
  conversation_control_mode: 'balanced',
  use_active_deck: true,
  use_all_videos: true,
  contact_email: '',
  calendar_link: '',
  suggestion_instructions: '',
  entry_modal_logo_url: '',
  entry_modal_title: '',
  entry_modal_description: '',
  entry_modal_button_text: '',
  lead_capture_name: 'hidden',
  lead_capture_email: 'required',
  lead_capture_phone: 'hidden',
  lead_capture_business: 'hidden',
  background_screenshot_url: '',
  voice_settings: null,
});

const mapSalesAgentToFormData = (agent: SalesAgent | null | undefined): Partial<SalesAgent> => {
  if (!agent) {
    return createEmptyFormData();
  }

  return {
    ...createEmptyFormData(),
    ...agent,
    voice_settings: agent.voice_settings
      ? {
          ...agent.voice_settings,
        }
      : null,
  };
};

const VOICE_EMOTIONS = [
  { value: 'neutral', label: 'Neutral', description: 'Balanced and steady delivery.' },
  { value: 'content', label: 'Content', description: 'Warm, comfortable, and settled.' },
  { value: 'happy', label: 'Happy', description: 'Positive and upbeat without overdoing it.' },
  { value: 'confident', label: 'Confident', description: 'Assured, polished, and direct.' },
  { value: 'calm', label: 'Calm', description: 'Relaxed and measured pacing.' },
  { value: 'excited', label: 'Excited', description: 'High-energy and animated.' },
  { value: 'enthusiastic', label: 'Enthusiastic', description: 'Engaged and encouraging.' },
  { value: 'curious', label: 'Curious', description: 'Inquisitive and exploratory.' },
  { value: 'determined', label: 'Determined', description: 'Focused and purposeful.' },
  { value: 'surprised', label: 'Surprised', description: 'Expressive with a sense of discovery.' },
  { value: 'proud', label: 'Proud', description: 'Confident with celebratory emphasis.' },
  { value: 'peaceful', label: 'Peaceful', description: 'Soft, composed, and smooth.' },
  { value: 'skeptical', label: 'Skeptical', description: 'Questioning and slightly guarded.' },
  { value: 'sarcastic', label: 'Sarcastic', description: 'Dry and ironic.' },
  { value: 'frustrated', label: 'Frustrated', description: 'Tense with restrained irritation.' },
  { value: 'melancholic', label: 'Melancholic', description: 'Heavy and reflective.' },
  { value: 'anxious', label: 'Anxious', description: 'Uneasy and urgent.' },
  { value: 'sad', label: 'Sad', description: 'Low-energy and subdued.' },
  { value: 'scared', label: 'Scared', description: 'Nervous and apprehensive.' },
  { value: 'angry', label: 'Angry', description: 'Sharp and intense.' },
  { value: 'elated', label: 'Elated', description: 'Very elevated and celebratory.' },
];

const SUPPORTED_VOICE_EMOTIONS = new Set(VOICE_EMOTIONS.map((emotion) => emotion.value));
const VOICES_PER_PAGE = 8;
type VoiceFilter = 'recommended' | 'all' | 'feminine' | 'masculine';
type VoiceEditorPanel = 'voice' | 'style' | 'speed';
const RECOMMENDED_VOICE_KEYWORDS = [
  'brooke',
  'carson',
  'jacqueline',
  'katie',
  'kiefer',
  'ronald',
  'caroline',
  'cathy',
  'blake',
];
const NON_PROFESSIONAL_VOICE_KEYWORDS = [
  'disgust',
  'angry',
  'sarcastic',
  'scared',
  'anxious',
  'melancholic',
  'frustrated',
  'big sister',
  'coworker',
];

const normalizeVoiceName = (name?: string | null) =>
  (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const matchesVoiceGender = (voice: CartesiaVoice, target: 'feminine' | 'masculine') => {
  const normalizedGender = normalizeVoiceName(voice.gender);
  const normalizedDescription = normalizeVoiceName(voice.description);
  if (target === 'feminine') {
    return normalizedGender.includes('feminine') || normalizedDescription.includes('female');
  }
  return normalizedGender.includes('masculine') || normalizedDescription.includes('male');
};

const describeVoiceSpeed = (speed: number) => {
  if (speed <= 0.8) return 'Measured';
  if (speed >= 1.15) return 'Energetic';
  return 'Natural';
};

export const SalesAgentContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawVersionParam = searchParams.get('version');
  const versionNumber = rawVersionParam ? Number.parseInt(rawVersionParam, 10) : null;
  const rawBaseParam = searchParams.get('base');
  const rawCompareParam = searchParams.get('compare');
  const rawSelectedParam = searchParams.get('selected');
  const baseVersion = rawBaseParam ? Number.parseInt(rawBaseParam, 10) : null;
  const compareVersion = rawCompareParam ? Number.parseInt(rawCompareParam, 10) : null;
  const selectedVersion = rawSelectedParam ? Number.parseInt(rawSelectedParam, 10) : null;
  const isComparingTwo = baseVersion !== null && Number.isInteger(baseVersion) && baseVersion > 0
    && compareVersion !== null && Number.isInteger(compareVersion) && compareVersion > 0;
  const isViewingVersion = isComparingTwo || (versionNumber !== null && Number.isInteger(versionNumber) && versionNumber > 0);

  // Determine which version is the user's selection and which is the comparison
  // target (changeable via dropdown). Works for both two-version and single-version views.
  const resolvedSelectedVersion = isComparingTwo
    ? (selectedVersion === baseVersion ? baseVersion
      : selectedVersion === compareVersion ? compareVersion
      : compareVersion) // fallback: treat the higher version as selected
    : versionNumber;
  const compareTargetVersion = isComparingTwo && resolvedSelectedVersion !== null
    ? (resolvedSelectedVersion === baseVersion ? compareVersion : baseVersion)
    : null;
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [voiceSearch, setVoiceSearch] = useState('');
  const [styleSearch, setStyleSearch] = useState('');
  const [voiceFilter, setVoiceFilter] = useState<VoiceFilter>('recommended');
  const [voiceEditorPanel, setVoiceEditorPanel] = useState<VoiceEditorPanel>('voice');
  const [voicePage, setVoicePage] = useState(1);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const [activePreviewVoiceId, setActivePreviewVoiceId] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) {
      setMobileSectionView('detail');
    } else {
      setMobileSectionView('list');
    }
  }, [isMobile]);

  const [isEditing, setIsEditing] = useState(false);
  const [activeViewSection, setActiveViewSection] = useState<ConfigSection>('general');
  const [activeEditSection, setActiveEditSection] = useState<ConfigSection>('general');
  const [mobileSectionView, setMobileSectionView] = useState<'list' | 'detail'>('list');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundInputRef = React.useRef<HTMLInputElement>(null);

  const selectViewSection = (section: ConfigSection) => {
    setActiveViewSection(section);
    if (isMobile) {
      setMobileSectionView('detail');
    }
  };

  const sectionNavBase =
    'w-full rounded-lg border border-transparent px-3 py-3 text-left';
  const sectionNavActive = 'border-border bg-background text-foreground shadow-sm';
  const workspaceRailButtonClass = (isActive: boolean) =>
    cn(sectionNavBase, isActive && sectionNavActive);
  const sectionButtonClass = (section: ConfigSection) =>
    workspaceRailButtonClass(activeViewSection === section);
  const [formData, setFormData] = useState<Partial<SalesAgent>>(() => createEmptyFormData());
  const currentSection = isEditing ? activeEditSection : activeViewSection;
  const currentSectionMeta = CONFIG_SECTIONS.find((section) => section.key === currentSection) ?? CONFIG_SECTIONS[0];

  // Fetch existing sales agent
  const { data: salesAgent, isLoading } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getSalesAgent(orgId);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const conversationModeOption = getConversationControlModeOption(
    salesAgent?.conversation_control_mode ?? formData.conversation_control_mode
  );

  const { data: customDomainState } = useQuery({
    queryKey: ['custom-domain', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await customDomainsService.getCustomDomain(orgId);
      if (response.success && response.data) return response.data;
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const liveDemoUrl = getLiveDemoUrl(salesAgent?.demo_slug, customDomainState?.custom_domain);
  const embeddedDemoUrl = getEmbeddedDemoUrl(salesAgent?.demo_slug);

  useEffect(() => {
    if (salesAgent) {
      setFormData(mapSalesAgentToFormData(salesAgent));
      setIsEditing(false);
    } else if (!isLoading) {
      setFormData(createEmptyFormData());
      setIsEditing(false);
    }
  }, [isLoading, salesAgent]);

  const { data: voiceCatalog, isLoading: isVoiceCatalogLoading } = useQuery({
    queryKey: ['cartesia-voices'],
    queryFn: async () => {
      const response = await cartesiaService.listVoices();
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load Cartesia voices');
      }
      return response.data.voices;
    },
    enabled: !isViewingVersion,
    staleTime: 5 * 60 * 1000,
  });
  // Fetch version diff when viewing a specific version
  const { data: versionDiff, isLoading: isVersionLoading } = useQuery({
    queryKey: ['prompt-version-diff', orgId, isComparingTwo ? `${baseVersion}-${compareVersion}` : versionNumber],
    queryFn: async () => {
      if (!orgId) return null;
      if (isComparingTwo) {
        const res = await promptVersionsService.getVersionDiffBetween(orgId, baseVersion!, compareVersion!);
        if (res.success && res.data) return res.data;
        return null;
      }
      if (versionNumber === null) return null;
      const res = await promptVersionsService.getVersionDiff(orgId, versionNumber);
      if (res.success && res.data) return res.data;
      return null;
    },
    enabled: !!orgId && isViewingVersion,
  });

  // Fetch versions list for the comparison target dropdown
  const { data: versionsList } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await promptVersionsService.listVersions(orgId);
      if (res.success && res.data) return res.data;
      return null;
    },
    enabled: !!orgId && isViewingVersion,
  });

  // Versions available in the "compare against" dropdown (exclude the selected version)
  const compareDropdownOptions = (versionsList?.versions ?? []).filter(
    (v) => v.version !== resolvedSelectedVersion,
  );
  const baseVersionMetadata: PromptVersion | null = versionDiff
    ? (versionsList?.versions ?? []).find((v) => v.version === versionDiff.base_version) ?? null
    : null;
  const compareVersionMetadata: PromptVersion | null = versionDiff
    ? (versionsList?.versions ?? []).find((v) => v.version === versionDiff.compare_version) ?? null
    : null;

  const handleCompareTargetChange = (newTargetValue: string) => {
    const newTarget = Number.parseInt(newTargetValue, 10);
    if (!resolvedSelectedVersion || isNaN(newTarget) || newTarget === compareTargetVersion) return;
    const base = Math.min(newTarget, resolvedSelectedVersion);
    const compare = Math.max(newTarget, resolvedSelectedVersion);
    navigate(
      `/agents/configure?base=${base}&compare=${compare}&selected=${resolvedSelectedVersion}`,
      { replace: true },
    );
  };

  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
      }
    };
  }, [previewAudio]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: SalesAgentUpdate) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.updateSalesAgent(orgId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update sales agent');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-agent', orgId] });
      setIsEditing(false);
      toast({
        title: 'Success',
        description: 'Sales agent updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const englishVoices = (voiceCatalog || []).filter((voice) => {
    const language = voice.language?.toLowerCase();
    return language === 'en' || language?.startsWith('en_') || language?.startsWith('en-');
  });

  const professionalEnglishVoices = englishVoices.filter((voice) => {
    const normalizedMetadata = normalizeVoiceName(`${voice.name || ''} ${voice.description || ''}`);
    return !NON_PROFESSIONAL_VOICE_KEYWORDS.some((keyword) => normalizedMetadata.includes(keyword));
  });

  const recommendedVoices = professionalEnglishVoices.filter((voice) => {
    const normalizedName = normalizeVoiceName(voice.name);
    return RECOMMENDED_VOICE_KEYWORDS.some((keyword) => normalizedName.includes(keyword));
  });
  const feminineVoices = professionalEnglishVoices.filter((voice) => matchesVoiceGender(voice, 'feminine'));
  const masculineVoices = professionalEnglishVoices.filter((voice) => matchesVoiceGender(voice, 'masculine'));
  const usingRecommendedFallback = voiceFilter === 'recommended' && recommendedVoices.length === 0;
  const visibleVoices = (() => {
    if (voiceFilter === 'recommended') {
      return usingRecommendedFallback ? professionalEnglishVoices : recommendedVoices;
    }
    if (voiceFilter === 'feminine') return feminineVoices;
    if (voiceFilter === 'masculine') return masculineVoices;
    return professionalEnglishVoices;
  })();

  const filteredVoices = visibleVoices.filter((voice) => {
    const query = voiceSearch.trim().toLowerCase();
    if (!query) return true;
    return [voice.name, voice.description, voice.language, voice.gender]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(query));
  });

  const selectedVoice = englishVoices.find((voice) => voice.id === formData.voice_settings?.voice_id)
    || (voiceCatalog || []).find((voice) => voice.id === formData.voice_settings?.voice_id)
    || null;
  const selectedEmotion = (formData.voice_settings?.emotion ?? '').trim() || 'confident';
  const selectedSpeed = formData.voice_settings?.speed ?? 0.9;
  const voicePanelCardClass = (panel: VoiceEditorPanel) => cn(
    'p-3 rounded-lg border text-left',
    voiceEditorPanel === panel
      ? 'border-primary bg-primary/5'
      : 'card-border bg-gray-50/50 dark:bg-zinc-900/50'
  );
  const totalVoicePages = Math.max(1, Math.ceil(filteredVoices.length / VOICES_PER_PAGE));
  const paginatedVoices = filteredVoices.slice((voicePage - 1) * VOICES_PER_PAGE, voicePage * VOICES_PER_PAGE);
  const filteredStyles = VOICE_EMOTIONS.filter((emotion) => {
    const query = styleSearch.trim().toLowerCase();
    if (!query) return true;
    return [emotion.label, emotion.value, emotion.description]
      .some((value) => value.toLowerCase().includes(query));
  });

  const updateVoiceSettings = (updates: Partial<VoiceSettings> | null) => {
    setFormData((prev) => {
      const current = prev.voice_settings ?? {
        provider: 'cartesia' as const,
        voice_id: '',
        speed: 0.9,
        emotion: 'confident',
        preview_source: 'cartesia' as const,
      };

      if (updates === null) {
        return { ...prev, voice_settings: null };
      }

      const nextVoiceSettings: VoiceSettings = {
        ...current,
        ...updates,
      };

      if (!nextVoiceSettings.voice_id) {
        return { ...prev, voice_settings: null };
      }

      return { ...prev, voice_settings: nextVoiceSettings };
    });
  };

  useEffect(() => {
    setVoicePage(1);
  }, [voiceSearch, voiceFilter]);

  useEffect(() => {
    if (voicePage > totalVoicePages) {
      setVoicePage(totalVoicePages);
    }
  }, [voicePage, totalVoicePages]);

  const selectVoice = (voice: CartesiaVoice) => {
    if (previewAudio) {
      previewAudio.pause();
      setActivePreviewVoiceId(null);
    }
    updateVoiceSettings({
      provider: 'cartesia',
      voice_id: voice.id,
      catalog_name: voice.name,
      language: voice.language,
      preview_source: 'cartesia',
    });
  };

  const handleVoicePreview = async (voice: CartesiaVoice) => {
    if (activePreviewVoiceId === voice.id && previewAudio) {
      previewAudio.pause();
      setActivePreviewVoiceId(null);
      return;
    }

    try {
      setPreviewingVoiceId(voice.id);
      if (previewAudio) {
        previewAudio.pause();
      }

      const authToken = apiClient.getAuthToken();
      const previewParams = new URLSearchParams({
        speed: selectedSpeed.toFixed(2),
        emotion: selectedEmotion,
      });
      const response = await fetch(
        `${apiClient.getBaseUrl()}/api/integrations/cartesia/voices/${voice.id}/preview?${previewParams.toString()}`,
        {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        }
      );

      if (!response.ok) {
        throw new Error(`Preview failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audio.onended = () => {
        URL.revokeObjectURL(objectUrl);
        setActivePreviewVoiceId((current) => (current === voice.id ? null : current));
      };

      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setActivePreviewVoiceId(null);
      };

      setPreviewAudio(audio);
      setActivePreviewVoiceId(voice.id);
      await audio.play();
    } catch (err) {
      toast({
        title: 'Preview unavailable',
        description: err instanceof Error ? err.message : 'Unable to play this voice preview right now.',
        variant: 'destructive',
      });
      setActivePreviewVoiceId(null);
    } finally {
      setPreviewingVoiceId(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!salesAgent) {
      toast({
        title: 'Agent not ready',
        description: 'Create an agent from Versions before making changes here.',
        variant: 'destructive',
      });
      return;
    }

    if (formData.voice_settings?.emotion?.trim() && !SUPPORTED_VOICE_EMOTIONS.has(formData.voice_settings.emotion.trim().toLowerCase())) {
      toast({
        title: 'Unsupported speaking style',
        description: 'Use one of the supported voice styles for now, or pick from the suggested options below.',
        variant: 'destructive',
      });
      return;
    }

    const payload: SalesAgentUpdate = {
      is_active: formData.is_active ?? true,
      conversation_control_mode: formData.conversation_control_mode || 'balanced',
      use_active_deck: formData.use_active_deck !== false,
      use_all_videos: formData.use_all_videos !== false,
      contact_email: formData.contact_email || '',
      calendar_link: formData.calendar_link || '',
      suggestion_instructions: formData.suggestion_instructions || '',
      entry_modal_logo_url: formData.entry_modal_logo_url || '',
      entry_modal_title: formData.entry_modal_title || '',
      entry_modal_description: formData.entry_modal_description || '',
      entry_modal_button_text: formData.entry_modal_button_text || '',
      lead_capture_name: formData.lead_capture_name || 'hidden',
      lead_capture_email: formData.lead_capture_email || 'required',
      lead_capture_phone: formData.lead_capture_phone || 'hidden',
      lead_capture_business: formData.lead_capture_business || 'hidden',
      background_screenshot_url: formData.background_screenshot_url || '',
      voice_settings: formData.voice_settings?.voice_id
        ? {
            provider: 'cartesia',
            voice_id: formData.voice_settings.voice_id,
            speed: formData.voice_settings.speed ?? 0.9,
            emotion: formData.voice_settings.emotion?.trim() ? formData.voice_settings.emotion.trim().toLowerCase() : 'confident',
          }
        : null,
    };

    updateMutation.mutate(payload);
  };

  const handleImageUpload = async (
    file: File,
    field: 'entry_modal_logo_url' | 'background_screenshot_url',
    fileType: string,
    setUploading: (v: boolean) => void,
  ) => {
    if (!orgId) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Allowed formats: PNG, JPG, WebP, GIF, SVG', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('organizationId', orgId);
      formPayload.append('fileType', fileType);
      formPayload.append('file', file);
      if (salesAgent?.id) formPayload.append('agentKey', salesAgent.id);
      const res = await apiClient.post<FileUploadResponse>('/api/storage/upload', formPayload);
      if (!res.success || !res.data?.url) {
        throw new Error(res.error || 'Upload failed');
      }
      setFormData((prev) => ({ ...prev, [field]: res.data!.url }));
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  if (isLoading || !currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-subtle">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Loading sales agent...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isViewingVersion && !salesAgent) {
    return (
      <div className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 pb-8 pt-4">
          <div className="max-w-3xl overflow-hidden rounded-xl border border-border bg-background shadow-sm">
            <div className="border-b border-border px-6 py-5">
              <h2 className="text-2xl font-semibold tracking-tight text-heading">Agent</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Manage how your agent runs and what visitors see before a session starts.
              </p>
            </div>

            <div className="px-6 py-6">
              <div className="grid gap-5 rounded-2xl border border-border bg-card p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-heading">No agent yet</p>
                  <p className="text-sm text-muted-foreground">Create your agent to start running sessions.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => navigate('/agents/create-version')}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Create Agent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/agents')}
                  >
                    Back to Agent Versions
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 pb-8 pt-4">
          <Tabs
            value={activeEditSection}
            onValueChange={(value) => {
              setActiveEditSection(value as ConfigSection);
              if (isMobile) {
                setMobileSectionView('detail');
              }
            }}
            className="flex flex-col h-full"
          >
            <div className={cn("flex flex-col gap-5", !isViewingVersion && "xl:grid xl:grid-cols-[280px_minmax(0,1fr)]")}>
              {/* Sidebar Navigation */}
              {!isViewingVersion && (!isMobile || mobileSectionView === 'list') && (
                <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:self-start">
                  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                    <div className="border-b border-border px-4 py-4">
                      <h2 className="text-base font-semibold tracking-tight text-heading">Agent</h2>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        Manage how your agent runs and what visitors see before a session starts.
                      </p>
                    </div>

                    <div className="p-3">
                      {isEditing ? (
                        <nav className="flex flex-col gap-2">
                          {CONFIG_SECTIONS.map((section) => (
                            <button
                              key={section.key}
                              type="button"
                              className={workspaceRailButtonClass(activeEditSection === section.key)}
                              onClick={() => setActiveEditSection(section.key)}
                            >
                              <div className="flex items-start gap-3">
                                <section.icon className={cn(
                                  'mt-0.5 h-4 w-4 shrink-0',
                                  activeEditSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                )} />
                                <div className="min-w-0">
                                  <p className={cn(
                                    'text-sm font-medium',
                                    activeEditSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                  )}>
                                    {section.label}
                                  </p>
                                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{section.description}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </nav>
                      ) : (
                        <nav className="flex flex-col gap-2">
                          {CONFIG_SECTIONS.map((section) => (
                            <button
                              key={section.key}
                              type="button"
                              className={sectionButtonClass(section.key)}
                              onClick={() => selectViewSection(section.key)}
                            >
                              <div className="flex items-start gap-3">
                                <section.icon className={cn(
                                  'mt-0.5 h-4 w-4 shrink-0',
                                  activeViewSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                )} />
                                <div className="min-w-0">
                                  <p className={cn(
                                    'text-sm font-medium',
                                    activeViewSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                  )}>
                                    {section.label}
                                  </p>
                                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{section.description}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </nav>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="border-t border-border px-4 py-4">
                        <p className="text-xs font-medium text-muted-foreground">Preview</p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!liveDemoUrl}
                            onClick={() => liveDemoUrl && window.open(liveDemoUrl, '_blank')}
                            className="flex-1 justify-center gap-1.5 h-8 text-xs rounded-md border-[hsl(var(--input))] bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            Full Page
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!embeddedDemoUrl}
                            onClick={() => embeddedDemoUrl && window.open(embeddedDemoUrl, '_blank')}
                            className="flex-1 justify-center gap-1.5 h-8 text-xs rounded-md border-[hsl(var(--input))] bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            Embedded
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </aside>
              )}

              {/* Main Content Area */}
              {(!isMobile || mobileSectionView === 'detail') && (
                <main className="min-w-0 overflow-hidden rounded-xl border border-border bg-background shadow-sm">
                  {isMobile && (
                    <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMobileSectionView('list')}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                      </Button>
                      <span className="text-sm text-muted-foreground">Agent</span>
                    </div>
                  )}
                  {!isViewingVersion && (
                    <div className="border-b border-border px-6 py-5 lg:px-8">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                          {isEditing && (
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em]">
                                Editing
                              </Badge>
                            </div>
                          )}
                          <div>
                            <h2 className="text-2xl font-semibold tracking-tight text-heading">{currentSectionMeta.label}</h2>
                            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                              {currentSectionMeta.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                className="rounded-xl"
                                onClick={() => {
                                  setIsEditing(false);
                                  setFormData(mapSalesAgentToFormData(salesAgent));
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                form="agent-form"
                                disabled={updateMutation.isPending}
                                className="rounded-xl"
                              >
                                {updateMutation.isPending ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving
                                  </>
                                ) : (
                                  <>
                                    <Save className="mr-2 h-4 w-4" />
                                    Save Changes
                                  </>
                                )}
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              onClick={() => {
                                setFormData(mapSalesAgentToFormData(salesAgent));
                                setIsEditing(true);
                                setActiveEditSection(activeViewSection);
                              }}
                              className="rounded-xl"
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                {isViewingVersion ? (
                  <div className="p-6 space-y-4">
                    {resolvedSelectedVersion !== null && compareDropdownOptions.length > 0 && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          Comparing v{resolvedSelectedVersion} against:
                        </span>
                        <Select
                          value={compareTargetVersion !== null ? String(compareTargetVersion) : undefined}
                          onValueChange={handleCompareTargetChange}
                        >
                          <SelectTrigger className="w-[180px] h-9">
                            <SelectValue placeholder="Select version" />
                          </SelectTrigger>
                          <SelectContent>
                            {compareDropdownOptions.map((v) => (
                              <SelectItem key={v.version} value={String(v.version)}>
                                v{v.version}
                                {v.status === 'live' ? ' (Live)' : v.status === 'staging' ? ' (Staging)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isVersionLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : versionDiff ? (
                      <PromptDiffView
                        data={versionDiff}
                        baseVersion={baseVersionMetadata}
                        compareVersion={compareVersionMetadata}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Unable to load version diff.</p>
                    )}
                  </div>
                ) : salesAgent && !isEditing ? (
                  <div className="px-6 py-8 lg:px-8">
                    {activeViewSection === 'general' && (
      <section className="max-w-4xl divide-y divide-border/60">
        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Active Status</p>
                            <p className="mt-1 text-sm text-muted-foreground">Enable or disable the sales agent for production use.</p>
                          </div>
                          <p className="text-sm text-muted-foreground md:justify-self-start">
                            {salesAgent.is_active ? 'The agent is currently available for production sessions.' : 'The agent is currently paused.'}
                          </p>
                          <Switch
                            checked={salesAgent.is_active}
                            onCheckedChange={() => {}}
                            className="data-[state=checked]:bg-primary disabled:opacity-100"
                            disabled
                          />
                        </div>

                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <p className="text-sm font-medium text-heading">Contact Email</p>
                            <p className="mt-1 text-sm text-muted-foreground">Used for follow-up and contact routing.</p>
                          </div>
                          <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-heading">
                            {salesAgent.contact_email || 'No contact email configured'}
                          </div>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'voice' && (
                      <section className="grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                        <div className="divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Selected Voice</p>
                              <p className="mt-1 text-sm text-muted-foreground">Primary voice used for live sessions.</p>
                            </div>
                            <div className="text-sm text-heading">{salesAgent.voice_settings?.catalog_name || 'No voice selected'}</div>
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Speaking Style</p>
                              <p className="mt-1 text-sm text-muted-foreground">Emotion profile applied to playback.</p>
                            </div>
                            <div className="text-sm text-heading capitalize">{salesAgent.voice_settings?.emotion || 'confident'}</div>
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Speed</p>
                              <p className="mt-1 text-sm text-muted-foreground">Cadence used for voice responses.</p>
                            </div>
                            <div className="text-sm text-heading">
                              {(salesAgent.voice_settings?.speed ?? 0.9).toFixed(2)} · {describeVoiceSpeed(salesAgent.voice_settings?.speed ?? 0.9)}
                            </div>
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Language</p>
                              <p className="mt-1 text-sm text-muted-foreground">Catalog language for the current voice.</p>
                            </div>
                            <div className="text-sm text-heading">{salesAgent.voice_settings?.language || 'Default'}</div>
                          </div>
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5">
                          <p className="text-xs font-medium text-muted-foreground">Preview</p>
                          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                            Hear the current configuration exactly as it will sound in live voice sessions.
                          </p>
                          <div className="mt-5 rounded-2xl border border-border bg-background px-4 py-3">
                            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Voice ID</p>
                            <p className="mt-2 break-all text-sm text-heading">{salesAgent.voice_settings?.voice_id || 'Not configured'}</p>
                          </div>

                          {selectedVoice && (
                            <Button
                              type="button"
                              variant="outline"
                              className="mt-5 w-full gap-2 rounded-xl"
                              onClick={() => handleVoicePreview(selectedVoice)}
                              disabled={previewingVoiceId === selectedVoice.id}
                            >
                              {previewingVoiceId === selectedVoice.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : activePreviewVoiceId === selectedVoice.id ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                              {activePreviewVoiceId === selectedVoice.id ? 'Pause Preview' : 'Play Preview'}
                            </Button>
                          )}
                        </div>
                      </section>
                    )}

                    {/* Content Section */}
                    {activeViewSection === 'content' && (
                      <section className="max-w-4xl divide-y divide-border/60">
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Active Presentation Deck</p>
                            <p className="mt-1 text-sm text-muted-foreground">Allow the agent to reference the active slide deck.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">Presentation content is {salesAgent.use_active_deck ? 'available' : 'excluded'} during sessions.</p>
                          <Badge variant={salesAgent.use_active_deck ? "default" : "secondary"}>
                            {salesAgent.use_active_deck ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">All Organization Videos</p>
                            <p className="mt-1 text-sm text-muted-foreground">Allow the agent to search video content.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">Video assets are {salesAgent.use_all_videos ? 'available' : 'excluded'} in the live experience.</p>
          <Badge variant={salesAgent.use_all_videos ? "default" : "secondary"}>
            {salesAgent.use_all_videos ? "Enabled" : "Disabled"}
          </Badge>
        </div>
                      </section>
                    )}

                    {activeViewSection === 'conversation' && (
                      <section className="max-w-4xl divide-y divide-border/60">
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Conversation Flow</p>
                            <p className="mt-1 text-sm text-muted-foreground">How strongly the agent should lead the demo once discovery is complete.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">{conversationModeOption.description}</p>
                          <Badge variant="secondary">
                            {conversationModeOption.label}
                          </Badge>
                        </div>
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <p className="text-sm font-medium text-heading">Suggestion Guidance</p>
                            <p className="mt-1 text-sm text-muted-foreground">Custom instructions for the follow-up suggestion chips shown during the demo.</p>
                          </div>
                          <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-heading whitespace-pre-wrap">
                            {salesAgent.suggestion_instructions || 'No custom suggestion guidance configured. The default suggestion behavior will be used.'}
                          </div>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'calendar' && (
                      <section className="max-w-4xl divide-y divide-border/60">
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <p className="text-sm font-medium text-heading">Calendar Link</p>
                            <p className="mt-1 text-sm text-muted-foreground">Booking destination for follow-up meetings.</p>
                          </div>
                          <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-heading">
                            {salesAgent.calendar_link || 'Using Hobbes default (cal.com/hobbes)'}
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Entry Modal Section */}
                    {activeViewSection === 'entryModal' && (
                      <section className="max-w-5xl">
                        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                          <div className="space-y-6">
                            <div className="rounded-xl border border-border bg-card p-5">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-xs font-medium text-muted-foreground">Logo</Label>
                                  <p className="mt-2 text-sm text-muted-foreground">Shown in the upper-left corner of the entry modal.</p>
                                </div>
                                <div className="shrink-0">
                                  {salesAgent.entry_modal_logo_url ? (
                                    <div className="inline-block rounded-2xl" style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                                      <img
                                        src={salesAgent.entry_modal_logo_url}
                                        alt="Entry modal logo"
                                        className="h-12 w-auto rounded-2xl object-contain"
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">Default logo</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-background">
                              <div className="grid gap-4 px-5 py-5 md:grid-cols-2 md:items-start">
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Title</p>
                                  <p className="mt-3 text-sm font-medium text-heading">{salesAgent.entry_modal_title || 'Experience Hobbes'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Button Text</p>
                                  <p className="mt-3 text-sm font-medium text-heading">{salesAgent.entry_modal_button_text || 'Start Your Personalized Demo'}</p>
                                </div>
                              </div>
                              <div className="border-t border-border px-5 py-5">
                                <p className="text-xs font-medium text-muted-foreground">Description</p>
                                <p className="mt-3 text-sm leading-relaxed text-heading">
                                  {salesAgent.entry_modal_description || 'Discover how a Hobbes agent can transform your revenue engine.'}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl border border-border bg-card p-5">
                            <div>
                              <Label className="text-xs font-medium text-muted-foreground">Contact Fields</Label>
                              <p className="mt-2 text-sm text-muted-foreground">Choose what visitors are asked for before the demo starts.</p>
                            </div>

                            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                              {[
                                { label: 'Name', value: salesAgent.lead_capture_name },
                                { label: 'Email', value: salesAgent.lead_capture_email },
                                { label: 'Phone', value: salesAgent.lead_capture_phone },
                                { label: 'Company', value: salesAgent.lead_capture_business },
                              ].map((field) => (
                                <div key={field.label} className="rounded-xl border border-border bg-background px-4 py-3">
                                  <p className="text-sm font-medium text-heading">{field.label}</p>
                                  <p className="mt-1 text-sm text-muted-foreground">{getLeadCaptureStateLabel(field.value)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'launchState' && (
                      <section className="max-w-5xl space-y-8">
                        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                          <div className="rounded-xl border border-border bg-card p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <Label className="text-xs font-medium text-muted-foreground">Starting Image</Label>
                                <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                                  This image appears in the viewer before the demo content loads.
                                </p>
                              </div>
                              <Badge variant="outline" className="rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em]">
                                Viewer
                              </Badge>
                            </div>

                            <div className="mt-6 flex min-h-[280px] items-center justify-center rounded-xl border border-border bg-background p-6">
                              {salesAgent.background_screenshot_url ? (
                                <div
                                  className="flex w-full items-center justify-center rounded-xl"
                                  style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}
                                >
                                  <img
                                    src={salesAgent.background_screenshot_url}
                                    alt="Launch state preview"
                                    className="max-h-[220px] w-auto max-w-full rounded-xl object-contain"
                                  />
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Image className="mx-auto h-10 w-10 text-muted-foreground/50" />
                                  <p className="mt-4 text-sm font-medium text-heading">No starting image set</p>
                                  <p className="mt-1 text-sm text-muted-foreground">The viewer will open without a custom launch image.</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border border-border bg-card p-5">
                            <p className="text-xs font-medium text-muted-foreground">Usage</p>
                            <div className="mt-4 space-y-4 text-sm text-muted-foreground">
                              <p>Use this for a clean branded first impression before any slides or content appear.</p>
                              <p>Best results come from landscape imagery with strong focal balance and minimal embedded text.</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                  </div>
                ) : (
                  <form id="agent-form" onSubmit={handleSubmit} className="h-full">
                    <div className="px-6 py-8 lg:px-8">
                      <TabsContent value="general" className="mt-0 space-y-10">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="is_active" className="text-sm font-medium cursor-pointer text-heading">Active Status</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable or disable the sales agent for production use.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Changes take effect for live demo traffic immediately after saving.</p>
                            <Switch
                              id="is_active"
                              checked={formData.is_active || false}
                              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="contact_email" className="text-sm font-medium text-heading">Contact Email</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Used for sales-agent follow-up and contact routing.</p>
                            </div>
                            <Input
                              id="contact_email"
                              type="email"
                              value={formData.contact_email || ''}
                              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                              placeholder="team@yourcompany.com"
                              className="h-11 rounded-2xl"
                            />
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="voice" className="mt-0 space-y-6">
                        <div className="max-w-5xl">
                          <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('voice')}
                                className={voicePanelCardClass('voice')}
                                aria-pressed={voiceEditorPanel === 'voice'}
                              >
                                <Label className="text-xs text-subtle mb-1 block">Selected Voice</Label>
                                <p className="text-sm font-medium">{selectedVoice?.name || 'No voice selected'}</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('style')}
                                className={voicePanelCardClass('style')}
                                aria-pressed={voiceEditorPanel === 'style'}
                              >
                                <Label className="text-xs text-subtle mb-1 block">Speaking Style</Label>
                                <p className="text-sm font-medium capitalize">{selectedEmotion}</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('speed')}
                                className={voicePanelCardClass('speed')}
                                aria-pressed={voiceEditorPanel === 'speed'}
                              >
                                <Label className="text-xs text-subtle mb-1 block">Speed</Label>
                                <p className="text-sm font-medium">{selectedSpeed.toFixed(2)} · {describeVoiceSpeed(selectedSpeed)}</p>
                              </button>
                            </div>

                            <div className="rounded-lg border card-border bg-gray-50/30 dark:bg-zinc-900/30 p-4">
                              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                  <Label className="text-xs text-subtle uppercase tracking-wider font-semibold mb-1 block">Voice Preview</Label>
                                  <p className="text-sm text-body">
                                    {selectedVoice
                                      ? `Play ${selectedVoice.name} using ${selectedEmotion} at ${selectedSpeed.toFixed(2)} speed.`
                                      : 'Select a voice to preview the current speaking style and speed.'}
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="gap-2 md:self-start"
                                  onClick={() => selectedVoice && handleVoicePreview(selectedVoice)}
                                  disabled={!selectedVoice || previewingVoiceId === selectedVoice.id}
                                >
                                  {selectedVoice && previewingVoiceId === selectedVoice.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : selectedVoice && activePreviewVoiceId === selectedVoice.id ? (
                                    <Pause className="h-4 w-4" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                  {selectedVoice && activePreviewVoiceId === selectedVoice.id ? 'Pause Sample' : 'Play Sample'}
                                </Button>
                              </div>
                            </div>

                            {voiceEditorPanel === 'voice' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between gap-3">
                                <Label className="text-sm font-semibold">Available Voices</Label>
                              </div>
                              <Tabs
                                value={voiceFilter}
                                onValueChange={(value) => setVoiceFilter(value as VoiceFilter)}
                                className="w-full"
                              >
                                <TabsList className="inline-flex h-9 w-auto">
                                  <TabsTrigger value="recommended">Recommended</TabsTrigger>
                                  <TabsTrigger value="all">All</TabsTrigger>
                                  <TabsTrigger value="feminine">Feminine</TabsTrigger>
                                  <TabsTrigger value="masculine">Masculine</TabsTrigger>
                                </TabsList>
                              </Tabs>
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  value={voiceSearch}
                                  onChange={(e) => setVoiceSearch(e.target.value)}
                                  placeholder="Search voices"
                                  className="h-10 pl-9"
                                />
                              </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              {isVoiceCatalogLoading ? (
                                <div className="col-span-full flex items-center justify-center rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 py-10 text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                </div>
                              ) : filteredVoices.length === 0 ? (
                                <div className="col-span-full rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 p-6 text-center">
                                  <p className="text-sm font-medium text-heading">No voices match this filter</p>
                                  <p className="mt-1 text-sm text-subtle">Try a different search term or switch tabs.</p>
                                </div>
                              ) : (
                                paginatedVoices.map((voice) => {
                                  const isSelected = formData.voice_settings?.voice_id === voice.id;
                                  return (
                                    <div
                                      key={voice.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => selectVoice(voice)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          selectVoice(voice);
                                        }
                                      }}
                                      className={cn(
                                        'rounded-lg border p-4',
                                        isSelected
                                          ? 'border-primary bg-primary/5'
                                          : 'card-border bg-gray-50/30 dark:bg-zinc-900/30'
                                      )}
                                    >
                                      <div className="space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <p className="text-sm font-semibold text-heading">{voice.name}</p>
                                          {isSelected && (
                                            <Badge className="text-[10px] uppercase tracking-wide">Selected</Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-subtle line-clamp-2">
                                          {voice.description || 'Voice preview available.'}
                                        </p>
                                      </div>

                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {voice.language && (
                                          <Badge variant="outline" className="gap-1">
                                            <Globe2 className="h-3 w-3" />
                                            {voice.language}
                                          </Badge>
                                        )}
                                        {voice.gender && (
                                          <Badge variant="outline">{voice.gender}</Badge>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            {filteredVoices.length > 0 && (
                              <div className="flex items-center justify-between pt-1">
                                <p className="text-xs text-subtle">
                                  Showing {(voicePage - 1) * VOICES_PER_PAGE + 1}-{Math.min(voicePage * VOICES_PER_PAGE, filteredVoices.length)} of {filteredVoices.length}
                                </p>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVoicePage((page) => Math.max(1, page - 1))}
                                    disabled={voicePage === 1}
                                    className="h-8 px-2"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </Button>
                                  <span className="text-xs text-subtle">
                                    Page {voicePage} of {totalVoicePages}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVoicePage((page) => Math.min(totalVoicePages, page + 1))}
                                    disabled={voicePage === totalVoicePages}
                                    className="h-8 px-2"
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                            </div>
                            )}

                            {voiceEditorPanel === 'style' && (
                            <div className="space-y-3">
                              <Label className="text-sm font-semibold">Search Styles</Label>
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  value={styleSearch}
                                  onChange={(e) => setStyleSearch(e.target.value)}
                                  placeholder="Search styles"
                                  className="h-10 pl-9"
                                />
                              </div>
                              <div className="grid gap-3 md:grid-cols-2">
                                {filteredStyles.map((emotion) => {
                                  const isActive = selectedEmotion === emotion.value;
                                  return (
                                    <button
                                      key={emotion.value}
                                      type="button"
                                      onClick={() => updateVoiceSettings({ emotion: emotion.value, provider: 'cartesia', voice_id: formData.voice_settings?.voice_id || '' })}
                                      className={cn(
                                        'rounded-lg border p-4 text-left',
                                        isActive
                                          ? 'border-primary bg-primary/5'
                                          : 'card-border bg-gray-50/30 dark:bg-zinc-900/30'
                                      )}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-heading">{emotion.label}</p>
                                          <p className="mt-1 text-xs text-subtle">{emotion.description}</p>
                                        </div>
                                        {isActive && (
                                          <Badge className="text-[10px] uppercase tracking-wide">Selected</Badge>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            )}

                            {voiceEditorPanel === 'speed' && (
                            <div className="space-y-3 rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 p-4">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-sm font-semibold">Speed</Label>
                                  <p className="text-xs text-subtle">Tune cadence from measured to energetic.</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-heading">{selectedSpeed.toFixed(2)}</p>
                                  <p className="text-xs text-subtle">{describeVoiceSpeed(selectedSpeed)}</p>
                                </div>
                              </div>
                              <Slider
                                value={[selectedSpeed]}
                                min={0.6}
                                max={1.5}
                                step={0.05}
                                onValueChange={(value) => updateVoiceSettings({
                                  provider: 'cartesia',
                                  voice_id: formData.voice_settings?.voice_id || '',
                                  speed: Number(value[0].toFixed(2)),
                                })}
                              />
                              <div className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                                <span>Measured</span>
                                <span>Natural</span>
                                <span>Energetic</span>
                              </div>
                            </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="content" className="mt-0 space-y-6">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="use_active_deck" className="text-sm font-medium cursor-pointer text-heading">Active Presentation Deck</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable the agent to reference slide content.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Use the currently active deck during the demo narrative.</p>
                            <Switch
                              id="use_active_deck"
                              checked={formData.use_active_deck !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_active_deck: checked })}
                            />
                          </div>

                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="use_all_videos" className="text-sm font-medium cursor-pointer text-heading">All Organization Videos</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable the agent to search video content.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Include organization video assets in the live retrieval set.</p>
                            <Switch
                              id="use_all_videos"
                              checked={formData.use_all_videos !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_all_videos: checked })}
                            />
                          </div>

                        </div>
                      </TabsContent>

                      <TabsContent value="conversation" className="mt-0 space-y-6">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="conversation_control_mode" className="text-sm font-medium text-heading">Conversation Flow</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Choose how assertively the agent should guide the visitor through the demo.</p>
                            </div>
                            <div className="space-y-3">
                              <Select
                                value={formData.conversation_control_mode || 'balanced'}
                                onValueChange={(value) => setFormData({ ...formData, conversation_control_mode: value as SalesAgent['conversation_control_mode'] })}
                              >
                                <SelectTrigger id="conversation_control_mode" className="h-11 rounded-2xl">
                                  <SelectValue placeholder="Select conversation style" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CONVERSATION_CONTROL_MODE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="rounded-2xl border border-border bg-card px-4 py-3">
                                <p className="text-sm font-medium text-heading">
                                  {getConversationControlModeOption(formData.conversation_control_mode).label}
                                </p>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {getConversationControlModeOption(formData.conversation_control_mode).description}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="suggestion_instructions" className="text-sm font-medium text-heading">Suggestion Guidance</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Shape how follow-up suggestion chips guide the conversation after each turn.</p>
                            </div>
                            <div className="space-y-2">
                              <Textarea
                                id="suggestion_instructions"
                                value={formData.suggestion_instructions || ''}
                                onChange={(e) => setFormData({ ...formData, suggestion_instructions: e.target.value })}
                                placeholder="Example: Keep one suggestion focused on ROI, one on implementation, and avoid pricing unless the visitor asks."
                                className="min-h-[140px] rounded-2xl"
                                maxLength={2000}
                              />
                              <p className="text-xs text-subtle">
                                This guidance applies only to suggestion chips, not the core spoken prompt.
                              </p>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="calendar" className="mt-0 space-y-6">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="calendar_link" className="text-sm font-medium text-heading">Calendar Link</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Booking destination used for follow-up meetings.</p>
                            </div>
                            <div className="space-y-2">
                              <Input
                                id="calendar_link"
                                type="url"
                                value={formData.calendar_link || ''}
                                onChange={(e) => setFormData({ ...formData, calendar_link: e.target.value })}
                                placeholder="https://cal.com/your-company/demo"
                                className="h-11 rounded-2xl"
                              />
                              <p className="text-xs text-subtle">Leave blank to use the Hobbes default booking destination.</p>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="entryModal" className="mt-0 space-y-6">
                        <div className="max-w-5xl">
                          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                            <div className="space-y-6">
                              <div className="rounded-xl border border-border bg-card p-5">
                                <div className="flex items-center justify-between gap-4">
                                  <div>
                                    <Label className="text-sm font-medium text-heading">Logo</Label>
                                    <p className="mt-1 text-sm text-muted-foreground">Shown in the upper-left corner of the entry modal.</p>
                                  </div>
                                  <div className="shrink-0">
                                    {formData.entry_modal_logo_url ? (
                                      <div className="relative rounded-2xl border card-border" style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                                        <img
                                          src={formData.entry_modal_logo_url}
                                          alt="Entry modal logo"
                                          className="h-12 w-auto object-contain rounded-2xl"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                          onClick={() => setFormData({ ...formData, entry_modal_logo_url: '' })}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex h-12 w-24 items-center justify-center rounded-2xl border-2 border-dashed border-muted-foreground/25 bg-gray-50/50 dark:bg-zinc-900/50">
                                        <Image className="h-6 w-6 text-muted-foreground/50" />
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-logo-url.com/logo.png"
                                    value={formData.entry_modal_logo_url || ''}
                                    onChange={(e) => setFormData({ ...formData, entry_modal_logo_url: e.target.value })}
                                    className="h-11 min-w-[240px] flex-1 rounded-2xl"
                                  />
                                  <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'entry_modal_logo_url', 'agent_logo', setIsUploadingLogo);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingLogo}
                                    onClick={() => logoInputRef.current?.click()}
                                    className="h-11 gap-1.5 rounded-2xl"
                                  >
                                    {isUploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingLogo ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                              </div>

                              <div className="rounded-xl border border-border bg-background">
                                <div className="grid gap-4 px-5 py-5 md:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label htmlFor="entry_modal_title" className="text-sm font-medium text-heading">Title</Label>
                                    <Input
                                      id="entry_modal_title"
                                      value={formData.entry_modal_title || ''}
                                      onChange={(e) => setFormData({ ...formData, entry_modal_title: e.target.value })}
                                      placeholder="Experience Acme Corp"
                                      className="h-11 rounded-2xl"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label htmlFor="entry_modal_button_text" className="text-sm font-medium text-heading">Button Text</Label>
                                    <Input
                                      id="entry_modal_button_text"
                                      value={formData.entry_modal_button_text || ''}
                                      onChange={(e) => setFormData({ ...formData, entry_modal_button_text: e.target.value })}
                                      placeholder="Start Your Personalized Demo"
                                      className="h-11 rounded-2xl"
                                    />
                                  </div>
                                </div>

                                <div className="border-t border-border px-5 py-5">
                                  <div className="space-y-2">
                                    <Label htmlFor="entry_modal_description" className="text-sm font-medium text-heading">Description</Label>
                                    <Textarea
                                      id="entry_modal_description"
                                      value={formData.entry_modal_description || ''}
                                      onChange={(e) => setFormData({ ...formData, entry_modal_description: e.target.value })}
                                      placeholder="Discover how our AI can transform your revenue engine and supercharge your sales process."
                                      rows={3}
                                      className="resize-none rounded-2xl"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-card p-5">
                              <div>
                                <Label className="text-sm font-medium text-heading">Contact Fields</Label>
                                <p className="mt-1 text-sm text-muted-foreground">Choose what visitors are asked for before the demo starts.</p>
                              </div>

                              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                                {[
                                  { key: 'lead_capture_name', label: 'Name' },
                                  { key: 'lead_capture_email', label: 'Email' },
                                  { key: 'lead_capture_phone', label: 'Phone' },
                                  { key: 'lead_capture_business', label: 'Company' },
                                ].map((field) => (
                                  <div key={field.key} className="rounded-xl border border-border bg-background px-4 py-4">
                                    <p className="text-sm font-medium text-heading">{field.label}</p>
                                    <Select
                                      value={(formData[field.key as keyof SalesAgent] as LeadCaptureState | undefined) || 'hidden'}
                                      onValueChange={(value: LeadCaptureState) => setFormData({ ...formData, [field.key]: value })}
                                    >
                                      <SelectTrigger className="mt-3 w-full rounded-2xl">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {LEAD_CAPTURE_FIELD_OPTIONS.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="launchState" className="mt-0 space-y-6">
                        <div className="max-w-5xl space-y-8">
                          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                            <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                              <div>
                                <Label className="text-sm font-medium text-heading">Starting Image</Label>
                                <p className="mt-1 text-sm text-muted-foreground">Displayed in the viewer before any demo content is presented.</p>
                              </div>

                              <div className="flex min-h-[260px] items-center justify-center rounded-xl border border-border bg-background p-6">
                                {formData.background_screenshot_url ? (
                                  <div
                                    className="relative flex w-full items-center justify-center rounded-xl"
                                    style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}
                                  >
                                    <img
                                      src={formData.background_screenshot_url}
                                      alt="Background screenshot"
                                      className="max-h-[220px] w-auto max-w-full object-contain rounded-xl"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                      onClick={() => setFormData({ ...formData, background_screenshot_url: '' })}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <Image className="mx-auto h-10 w-10 text-muted-foreground/50" />
                                    <p className="mt-4 text-sm font-medium text-heading">No starting image selected</p>
                                    <p className="mt-1 text-sm text-muted-foreground">Add an image to create a more polished launch state.</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                              <div>
                                <Label className="text-sm font-medium text-heading">Asset Source</Label>
                                <p className="mt-1 text-sm text-muted-foreground">Provide an image URL or upload a file directly.</p>
                              </div>
                              <div className="space-y-2">
                                <Input
                                  type="url"
                                  placeholder="https://example.com/screenshot.png"
                                  value={formData.background_screenshot_url || ''}
                                  onChange={(e) => setFormData({ ...formData, background_screenshot_url: e.target.value })}
                                  className="h-11 rounded-2xl"
                                />
                                <input
                                  ref={backgroundInputRef}
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file, 'background_screenshot_url', 'background_screenshot', setIsUploadingBackground);
                                    e.target.value = '';
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={isUploadingBackground}
                                  onClick={() => backgroundInputRef.current?.click()}
                                  className="h-11 w-full gap-1.5 rounded-2xl"
                                >
                                  {isUploadingBackground ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                  {isUploadingBackground ? 'Uploading...' : 'Upload Image'}
                                </Button>
                                <p className="text-xs text-subtle">PNG, JPG, WebP, GIF, or SVG. Landscape imagery works best here.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>
                    </div>
                  </form>
                )}
                </main>
              )}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default SalesAgentContent;
